import { useSignal } from "@preact/signals";
import type { LoaderArgs, PageArgs } from "bunfx";
import { RedirectError } from "bunfx";
import { useEffect, useRef } from "preact/hooks";
import {
  IcoChevronRight,
  IcoImage,
  IcoMail,
  IcoSpinner,
  IcoStar,
  IcoUndo,
  IcoUser,
  IcoUsers,
} from "../../components/icons";
import {
  BrandedLayout,
  Button,
  ImageUpload,
  useFocusTrap,
} from "../../components/ui";
import { usePageTitle } from "../../hooks";
import { rpc } from "../../rpc";
import { showError } from "../../stores/errors";
import type { MimicState } from "../../stores/mimic";
import type { BrandColor, User } from "../../types";
import { isAdmin } from "../../types";
import { getErrorMessage } from "../../utils/errors";

type SettingsPageState = {
  currentUser: User;
  mimic: MimicState;
  organization: {
    id: string;
    name: string;
    logoUrl: string | null;
    brandColor: BrandColor;
    memberCount: number;
    formCount: number;
    hasReports: boolean;
    emailDisplayName: string | null;
    emailReplyTo: string | null;
    valuesDigestEmail: string | null;
    mailFrom: string;
  };
};

export async function load(args: LoaderArgs): Promise<SettingsPageState> {
  const orgId = args.params.orgId ?? "";

  // Stage 1: Auth (must complete before data calls to get clean /login redirects)
  const [authResult, orgsResult] = await Promise.all([
    rpc.auth.me({}),
    rpc.orgs.list({}),
  ]);

  if (!authResult.user) {
    throw new RedirectError("/login");
  }

  // Stage 2: Data (safe now that auth is validated)
  const orgResult = await rpc.orgs.get({ id: orgId });

  // Only admins can view settings
  if (!isAdmin(orgResult.currentMember.role)) {
    throw new RedirectError("/");
  }

  // Get brand color from list endpoint (get endpoint doesn't return it)
  const orgFromList = orgsResult.organizations.find((o) => o.id === orgId);
  const brandColor = orgFromList?.brandColor ?? "indigo";

  const org = orgResult.organization;

  return {
    currentUser: authResult.user as User,
    mimic: authResult.mimic ?? null,
    organization: {
      id: org.id,
      name: org.name,
      logoUrl: org.logoUrl ?? null,
      brandColor: brandColor as BrandColor,
      memberCount: org.memberCount,
      formCount: org.formCount,
      hasReports: orgFromList?.hasReports ?? false,
      emailDisplayName: org.emailDisplayName ?? null,
      emailReplyTo: org.emailReplyTo ?? null,
      valuesDigestEmail: org.valuesDigestEmail ?? null,
      mailFrom: org.mailFrom ?? "notifications@pulse.mirasee.com",
    },
  };
}

export function Page({ state }: PageArgs<typeof load>) {
  usePageTitle("Settings");
  const { currentUser, mimic, organization } = state.value;
  const orgName = useSignal(organization.name);
  const logoUrl = useSignal(organization.logoUrl);
  const emailDisplayName = useSignal(organization.emailDisplayName);
  const emailReplyTo = useSignal(organization.emailReplyTo);
  const valuesDigestEmail = useSignal(organization.valuesDigestEmail);

  return (
    <BrandedLayout
      user={currentUser}
      mimic={mimic}
      orgId={organization.id}
      orgName={orgName.value}
      brandColor={organization.brandColor}
      activeTab="settings"
      isAdmin={true}
      formCount={organization.formCount}
      hasReports={organization.hasReports}
    >
      <div class="flex flex-col">
        <h2 class="text-sm font-semibold text-text-muted uppercase tracking-wide mb-4">
          Organization Settings
        </h2>

        <div class="rounded-xl bg-surface border border-border overflow-hidden">
          <div class="flex flex-col divide-y divide-border">
            {/* Logo Row */}
            <LogoRow
              orgId={organization.id}
              logoUrl={logoUrl.value}
              orgName={orgName.value}
              onUploadComplete={(url) => {
                logoUrl.value = url;
              }}
            />

            {/* Name Row */}
            <NameRow
              orgId={organization.id}
              initialName={organization.name}
              onNameChange={(newName) => {
                orgName.value = newName;
              }}
            />
          </div>
        </div>

        {/* Team Management Section */}
        <h2 class="text-sm font-semibold text-text-muted uppercase tracking-wide mb-4 mt-8">
          Team Management
        </h2>

        <div class="rounded-xl bg-surface border border-border overflow-hidden">
          <div class="flex flex-col divide-y divide-border">
            <a
              href={`/orgs/${organization.id}/members`}
              class="flex items-center gap-4 p-4 hover:bg-surface-hover transition-colors"
            >
              <div class="size-10 rounded-lg bg-surface-muted flex items-center justify-center flex-shrink-0">
                <IcoUser class="size-5 text-text-muted" />
              </div>
              <div class="flex-1 flex flex-col">
                <span class="text-text font-medium">Members</span>
                <span class="text-sm text-text-muted">
                  Manage team members and roles
                </span>
              </div>
              <IcoChevronRight class="size-5 text-text-muted" />
            </a>

            <a
              href={`/orgs/${organization.id}/groups`}
              class="flex items-center gap-4 p-4 hover:bg-surface-hover transition-colors"
            >
              <div class="size-10 rounded-lg bg-surface-muted flex items-center justify-center flex-shrink-0">
                <IcoUsers class="size-5 text-text-muted" />
              </div>
              <div class="flex-1 flex flex-col">
                <span class="text-text font-medium">Groups</span>
                <span class="text-sm text-text-muted">
                  Organize members for question targeting
                </span>
              </div>
              <IcoChevronRight class="size-5 text-text-muted" />
            </a>
          </div>
        </div>

        {/* Culture Section */}
        <h2 class="text-sm font-semibold text-text-muted uppercase tracking-wide mb-4 mt-8">
          Culture
        </h2>

        <div class="rounded-xl bg-surface border border-border overflow-hidden">
          <div class="flex flex-col divide-y divide-border">
            <a
              href={`/orgs/${organization.id}/values`}
              class="flex items-center gap-4 p-4 hover:bg-surface-hover transition-colors"
            >
              <div class="size-10 rounded-lg bg-surface-muted flex items-center justify-center flex-shrink-0">
                <IcoStar class="size-5 text-text-muted" />
              </div>
              <div class="flex-1 flex flex-col">
                <span class="text-text font-medium">Core Values</span>
                <span class="text-sm text-text-muted">
                  Define the values that guide your team
                </span>
              </div>
              <IcoChevronRight class="size-5 text-text-muted" />
            </a>

            <ValuesDigestEmailRow
              orgId={organization.id}
              initialValue={valuesDigestEmail.value}
              onChange={(value) => {
                valuesDigestEmail.value = value;
              }}
            />
          </div>
        </div>

        {/* Email Settings Section */}
        <h2 class="text-sm font-semibold text-text-muted uppercase tracking-wide mb-4 mt-8">
          Email Settings
        </h2>

        <div class="rounded-xl bg-surface border border-border overflow-hidden">
          <div class="flex flex-col divide-y divide-border">
            <EmailDisplayNameRow
              orgId={organization.id}
              orgName={orgName.value}
              initialValue={emailDisplayName.value}
              onChange={(value) => {
                emailDisplayName.value = value;
              }}
            />
            <EmailReplyToRow
              orgId={organization.id}
              initialValue={emailReplyTo.value}
              onChange={(value) => {
                emailReplyTo.value = value;
              }}
            />
          </div>
        </div>

        {/* Email Preview */}
        <div class="mt-4 p-4 rounded-lg bg-surface-muted border border-border">
          <div class="text-xs font-medium text-text-muted uppercase tracking-wide mb-2">
            Email Preview
          </div>
          <div class="text-sm text-text">
            <span class="text-text-muted">From:</span>{" "}
            <span class="font-medium">
              {emailDisplayName.value || `${orgName.value} via Pulse`}
            </span>{" "}
            <span class="text-text-muted">
              {"<"}
              {organization.mailFrom}
              {">"}
            </span>
          </div>
          {emailReplyTo.value && (
            <div class="text-sm text-text mt-1">
              <span class="text-text-muted">Reply-To:</span>{" "}
              <span class="font-medium">{emailReplyTo.value}</span>
            </div>
          )}
        </div>
      </div>
    </BrandedLayout>
  );
}

// ============================================================================
// Logo Row
// ============================================================================

function LogoRow({
  orgId,
  logoUrl,
  orgName,
  onUploadComplete,
}: {
  orgId: string;
  logoUrl: string | null;
  orgName: string;
  onUploadComplete: (url: string) => void;
}) {
  const showModal = useSignal(false);

  function openModal() {
    showModal.value = true;
  }

  function closeModal() {
    showModal.value = false;
  }

  const dialogRef = useFocusTrap<HTMLDivElement>({
    open: showModal.value,
    onEscape: closeModal,
  });

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        class="flex items-center gap-4 p-4 w-full text-left hover:bg-surface-hover transition-colors"
      >
        <div class="size-12 rounded-lg bg-surface-muted flex items-center justify-center overflow-hidden flex-shrink-0 ring-1 ring-border">
          {logoUrl ? (
            <img src={logoUrl} alt={orgName} class="size-full object-cover" />
          ) : (
            <IcoImage class="size-6 text-text-muted" />
          )}
        </div>
        <div class="flex flex-col">
          <span class="text-sm text-text-muted">Logo</span>
          <span class="text-text font-medium">
            {logoUrl ? "Change logo" : "Add a logo"}
          </span>
        </div>
      </button>

      {/* Logo Upload Modal */}
      {showModal.value && (
        <div
          class="fixed inset-0 z-[60] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="logo-upload-title"
        >
          {/* Backdrop */}
          <div
            class="absolute inset-0 bg-black/50 transition-opacity"
            onClick={closeModal}
            aria-hidden="true"
          />

          {/* Dialog */}
          <div
            ref={dialogRef}
            class="relative w-full max-w-sm bg-surface rounded-xl shadow-xl animate-scale-in"
          >
            <div class="p-6">
              <h3
                id="logo-upload-title"
                class="text-lg font-semibold text-text mb-4"
              >
                Organization Logo
              </h3>

              <ImageUpload
                label="Click to upload"
                helpText="PNG, JPG, or GIF up to 2MB"
                upload={{
                  getUploadUrl: () => rpc.files.getOrgLogoUploadUrl({ orgId }),
                  confirmUpload: () =>
                    rpc.files.confirmOrgLogoUpload({ orgId }),
                }}
                onUploadComplete={(url) => {
                  onUploadComplete(url);
                  closeModal();
                }}
              />

              <div class="mt-4">
                <Button variant="secondary" onClick={closeModal} class="w-full">
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ============================================================================
// Name Row
// ============================================================================

function NameRow({
  orgId,
  initialName,
  onNameChange,
}: {
  orgId: string;
  initialName: string;
  onNameChange: (newName: string) => void;
}) {
  const name = useSignal(initialName);
  const showModal = useSignal(false);
  const newName = useSignal(initialName);
  const isSaving = useSignal(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showModal.value && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [showModal.value]);

  function openModal() {
    newName.value = name.value;
    showModal.value = true;
  }

  function closeModal() {
    showModal.value = false;
    isSaving.value = false;
  }

  const dialogRef = useFocusTrap<HTMLDivElement>({
    open: showModal.value,
    onEscape: closeModal,
  });

  async function handleSave() {
    const trimmedName = newName.value.trim();
    if (!trimmedName || trimmedName === name.value) {
      closeModal();
      return;
    }

    isSaving.value = true;
    try {
      await rpc.orgs.update({ id: orgId, name: trimmedName });
      name.value = trimmedName;
      onNameChange(trimmedName);
      closeModal();
    } catch (err) {
      showError(getErrorMessage(err));
      isSaving.value = false;
    }
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (e.key === "Enter" && !isSaving.value) {
      e.preventDefault();
      handleSave();
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        class="flex flex-col gap-0.5 p-4 w-full text-left hover:bg-surface-hover transition-colors"
      >
        <span class="text-sm text-text-muted">Name</span>
        <span class="text-text font-medium">{name.value}</span>
      </button>

      {/* Name Edit Modal */}
      {showModal.value && (
        <div
          class="fixed inset-0 z-[60] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="org-name-edit-title"
        >
          {/* Backdrop */}
          <div
            class="absolute inset-0 bg-black/50 transition-opacity"
            onClick={closeModal}
            aria-hidden="true"
          />

          {/* Dialog */}
          <div
            ref={dialogRef}
            class="relative w-full max-w-sm bg-surface rounded-xl shadow-xl animate-scale-in"
          >
            <div class="p-6">
              <h3
                id="org-name-edit-title"
                class="text-lg font-semibold text-text mb-4"
              >
                Edit Organization Name
              </h3>
              <input
                ref={inputRef}
                type="text"
                value={newName.value}
                onInput={(e) => {
                  newName.value = (e.target as HTMLInputElement).value;
                }}
                onKeyDown={handleKeyDown}
                placeholder="Organization name"
                class="w-full px-3 py-2 rounded-lg border border-border bg-surface text-text placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-text-subtle"
                disabled={isSaving.value}
              />
            </div>

            <div class="flex gap-3 px-6 pb-6">
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving.value || !newName.value.trim()}
                class="flex-1 px-4 py-2 rounded-lg bg-[var(--brand-600)] hover:bg-[var(--brand-700)] text-white text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isSaving.value && <IcoSpinner class="size-4 animate-spin" />}
                Save
              </button>
              <button
                type="button"
                onClick={closeModal}
                disabled={isSaving.value}
                class="flex-1 px-4 py-2 rounded-lg text-sm font-medium text-text-muted hover:bg-surface-hover transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ============================================================================
// Email Display Name Row
// ============================================================================

function EmailDisplayNameRow({
  orgId,
  orgName,
  initialValue,
  onChange,
}: {
  orgId: string;
  orgName: string;
  initialValue: string | null;
  onChange: (value: string | null) => void;
}) {
  const displayName = useSignal(initialValue ?? "");
  const showModal = useSignal(false);
  const newDisplayName = useSignal(initialValue ?? "");
  const isSaving = useSignal(false);
  const error = useSignal<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showModal.value && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [showModal.value]);

  function openModal() {
    newDisplayName.value = displayName.value;
    showModal.value = true;
  }

  function closeModal() {
    showModal.value = false;
    isSaving.value = false;
    error.value = null;
  }

  const dialogRef = useFocusTrap<HTMLDivElement>({
    open: showModal.value,
    onEscape: closeModal,
  });

  async function handleSave() {
    const trimmed = newDisplayName.value.trim();
    const valueToSave = trimmed || null;

    // Skip if unchanged
    if (trimmed === displayName.value) {
      closeModal();
      return;
    }

    // Validate for invalid characters (matches server-side regex)
    if (valueToSave && /[<>"\\,;\r\n]/.test(valueToSave)) {
      error.value = "Display name contains invalid characters";
      return;
    }

    error.value = null;
    isSaving.value = true;
    try {
      await rpc.orgs.update({ id: orgId, emailDisplayName: valueToSave });
      displayName.value = trimmed;
      onChange(valueToSave);
      closeModal();
    } catch (err) {
      showError(getErrorMessage(err));
      isSaving.value = false;
    }
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (e.key === "Enter" && !isSaving.value) {
      e.preventDefault();
      handleSave();
    }
  }

  const placeholder = `${orgName} via Pulse`;

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        class="flex items-center gap-4 p-4 w-full text-left hover:bg-surface-hover transition-colors"
      >
        <div class="size-10 rounded-lg bg-surface-muted flex items-center justify-center flex-shrink-0">
          <IcoMail class="size-5 text-text-muted" />
        </div>
        <div class="flex-1 flex flex-col">
          <span class="text-sm text-text-muted">From Name</span>
          <span class="text-text font-medium">
            {displayName.value || (
              <span class="text-text-muted">{placeholder}</span>
            )}
          </span>
        </div>
      </button>

      {showModal.value && (
        <div
          class="fixed inset-0 z-[60] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="email-display-name-title"
        >
          <div
            class="absolute inset-0 bg-black/50 transition-opacity"
            onClick={closeModal}
            aria-hidden="true"
          />

          <div
            ref={dialogRef}
            class="relative w-full max-w-sm bg-surface rounded-xl shadow-xl animate-scale-in"
          >
            <div class="p-6">
              <h3
                id="email-display-name-title"
                class="text-lg font-semibold text-text mb-2"
              >
                Email Display Name
              </h3>
              <p class="text-sm text-text-muted mb-4">
                This appears as the "From" name in emails sent to your team.
              </p>
              <input
                ref={inputRef}
                type="text"
                value={newDisplayName.value}
                onInput={(e) => {
                  newDisplayName.value = (e.target as HTMLInputElement).value;
                  error.value = null;
                }}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                maxLength={100}
                class={`w-full px-3 py-2 rounded-lg border bg-surface text-text placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-text-subtle ${
                  error.value ? "border-red-500" : "border-border"
                }`}
                disabled={isSaving.value}
              />
              {error.value ? (
                <p class="text-xs text-red-500 mt-2">{error.value}</p>
              ) : (
                <p class="text-xs text-text-muted mt-2">
                  Leave empty to use default: {placeholder}
                </p>
              )}
            </div>

            <div class="flex gap-3 px-6 pb-6">
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving.value}
                class="flex-1 px-4 py-2 rounded-lg bg-[var(--brand-600)] hover:bg-[var(--brand-700)] text-white text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isSaving.value && <IcoSpinner class="size-4 animate-spin" />}
                Save
              </button>
              <button
                type="button"
                onClick={closeModal}
                disabled={isSaving.value}
                class="flex-1 px-4 py-2 rounded-lg text-sm font-medium text-text-muted hover:bg-surface-hover transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ============================================================================
// Email Reply-To Row
// ============================================================================

function EmailReplyToRow({
  orgId,
  initialValue,
  onChange,
}: {
  orgId: string;
  initialValue: string | null;
  onChange: (value: string | null) => void;
}) {
  const replyTo = useSignal(initialValue ?? "");
  const showModal = useSignal(false);
  const newReplyTo = useSignal(initialValue ?? "");
  const isSaving = useSignal(false);
  const error = useSignal<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showModal.value && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [showModal.value]);

  function openModal() {
    newReplyTo.value = replyTo.value;
    showModal.value = true;
  }

  function closeModal() {
    showModal.value = false;
    isSaving.value = false;
    error.value = null;
  }

  const dialogRef = useFocusTrap<HTMLDivElement>({
    open: showModal.value,
    onEscape: closeModal,
  });

  async function handleSave() {
    const trimmed = newReplyTo.value.trim();
    const valueToSave = trimmed || null;

    // Skip if unchanged
    if (trimmed === replyTo.value) {
      closeModal();
      return;
    }

    // Validate email format before server call
    if (valueToSave && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(valueToSave)) {
      error.value = "Please enter a valid email address";
      return;
    }

    error.value = null;
    isSaving.value = true;
    try {
      await rpc.orgs.update({ id: orgId, emailReplyTo: valueToSave });
      replyTo.value = trimmed;
      onChange(valueToSave);
      closeModal();
    } catch (err) {
      showError(getErrorMessage(err));
      isSaving.value = false;
    }
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (e.key === "Enter" && !isSaving.value) {
      e.preventDefault();
      handleSave();
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        class="flex items-center gap-4 p-4 w-full text-left hover:bg-surface-hover transition-colors"
      >
        <div class="size-10 rounded-lg bg-surface-muted flex items-center justify-center flex-shrink-0">
          <IcoUndo class="size-5 text-text-muted" />
        </div>
        <div class="flex-1 flex flex-col">
          <span class="text-sm text-text-muted">Reply-To Address</span>
          <span class="text-text font-medium">
            {replyTo.value || <span class="text-text-muted">Not set</span>}
          </span>
        </div>
      </button>

      {showModal.value && (
        <div
          class="fixed inset-0 z-[60] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="email-reply-to-title"
        >
          <div
            class="absolute inset-0 bg-black/50 transition-opacity"
            onClick={closeModal}
            aria-hidden="true"
          />

          <div
            ref={dialogRef}
            class="relative w-full max-w-sm bg-surface rounded-xl shadow-xl animate-scale-in"
          >
            <div class="p-6">
              <h3
                id="email-reply-to-title"
                class="text-lg font-semibold text-text mb-2"
              >
                Reply-To Address
              </h3>
              <p class="text-sm text-text-muted mb-4">
                When recipients reply to Pulse emails, their response will be
                sent to this address.
              </p>
              <input
                ref={inputRef}
                type="email"
                value={newReplyTo.value}
                onInput={(e) => {
                  newReplyTo.value = (e.target as HTMLInputElement).value;
                  error.value = null;
                }}
                onKeyDown={handleKeyDown}
                placeholder="support@yourcompany.com"
                maxLength={255}
                class={`w-full px-3 py-2 rounded-lg border bg-surface text-text placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-text-subtle ${
                  error.value ? "border-red-500" : "border-border"
                }`}
                disabled={isSaving.value}
              />
              {error.value ? (
                <p class="text-xs text-red-500 mt-2">{error.value}</p>
              ) : (
                <p class="text-xs text-text-muted mt-2">
                  Leave empty to disable reply-to header.
                </p>
              )}
            </div>

            <div class="flex gap-3 px-6 pb-6">
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving.value}
                class="flex-1 px-4 py-2 rounded-lg bg-[var(--brand-600)] hover:bg-[var(--brand-700)] text-white text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isSaving.value && <IcoSpinner class="size-4 animate-spin" />}
                Save
              </button>
              <button
                type="button"
                onClick={closeModal}
                disabled={isSaving.value}
                class="flex-1 px-4 py-2 rounded-lg text-sm font-medium text-text-muted hover:bg-surface-hover transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ============================================================================
// Values Digest Email Row
// ============================================================================

function ValuesDigestEmailRow({
  orgId,
  initialValue,
  onChange,
}: {
  orgId: string;
  initialValue: string | null;
  onChange: (value: string | null) => void;
}) {
  const digestEmail = useSignal(initialValue ?? "");
  const showModal = useSignal(false);
  const newDigestEmail = useSignal(initialValue ?? "");
  const isSaving = useSignal(false);
  const error = useSignal<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showModal.value && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [showModal.value]);

  function openModal() {
    newDigestEmail.value = digestEmail.value;
    showModal.value = true;
  }

  function closeModal() {
    showModal.value = false;
    isSaving.value = false;
    error.value = null;
  }

  const dialogRef = useFocusTrap<HTMLDivElement>({
    open: showModal.value,
    onEscape: closeModal,
  });

  async function handleSave() {
    const trimmed = newDigestEmail.value.trim();
    const valueToSave = trimmed || null;

    if (trimmed === digestEmail.value) {
      closeModal();
      return;
    }

    if (valueToSave && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(valueToSave)) {
      error.value = "Please enter a valid email address";
      return;
    }

    error.value = null;
    isSaving.value = true;
    try {
      await rpc.orgs.update({ id: orgId, valuesDigestEmail: valueToSave });
      digestEmail.value = trimmed;
      onChange(valueToSave);
      closeModal();
    } catch (err) {
      showError(getErrorMessage(err));
      isSaving.value = false;
    }
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (e.key === "Enter" && !isSaving.value) {
      e.preventDefault();
      handleSave();
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        class="flex items-center gap-4 p-4 w-full text-left hover:bg-surface-hover transition-colors"
      >
        <div class="size-10 rounded-lg bg-surface-muted flex items-center justify-center flex-shrink-0">
          <IcoMail class="size-5 text-text-muted" />
        </div>
        <div class="flex-1 flex flex-col">
          <span class="text-text font-medium">Weekly Values Digest</span>
          <span class="text-sm text-text-muted">
            {digestEmail.value
              ? `Sends to ${digestEmail.value} every Monday`
              : "Email a summary of values recognitions to the team"}
          </span>
        </div>
      </button>

      {showModal.value && (
        <div
          class="fixed inset-0 z-[60] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="values-digest-email-title"
        >
          <div
            class="absolute inset-0 bg-black/50 transition-opacity"
            onClick={closeModal}
            aria-hidden="true"
          />

          <div
            ref={dialogRef}
            class="relative w-full max-w-sm bg-surface rounded-xl shadow-xl animate-scale-in"
          >
            <div class="p-6">
              <h3
                id="values-digest-email-title"
                class="text-lg font-semibold text-text mb-2"
              >
                Weekly Values Digest
              </h3>
              <p class="text-sm text-text-muted mb-4">
                Every Monday, a summary of the previous week's values
                recognitions will be emailed to this address. Use a team
                distribution list so everyone sees it.
              </p>
              <input
                ref={inputRef}
                type="email"
                value={newDigestEmail.value}
                onInput={(e) => {
                  newDigestEmail.value = (e.target as HTMLInputElement).value;
                  error.value = null;
                }}
                onKeyDown={handleKeyDown}
                placeholder="theteam@yourcompany.com"
                maxLength={255}
                class={`w-full px-3 py-2 rounded-lg border bg-surface text-text placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-text-subtle ${
                  error.value ? "border-red-500" : "border-border"
                }`}
                disabled={isSaving.value}
              />
              {error.value ? (
                <p class="text-xs text-red-500 mt-2">{error.value}</p>
              ) : (
                <p class="text-xs text-text-muted mt-2">
                  Leave empty to disable the weekly digest.
                </p>
              )}
            </div>

            <div class="flex gap-3 px-6 pb-6">
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving.value}
                class="flex-1 px-4 py-2 rounded-lg bg-[var(--brand-600)] hover:bg-[var(--brand-700)] text-white text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isSaving.value && <IcoSpinner class="size-4 animate-spin" />}
                Save
              </button>
              <button
                type="button"
                onClick={closeModal}
                disabled={isSaving.value}
                class="flex-1 px-4 py-2 rounded-lg text-sm font-medium text-text-muted hover:bg-surface-hover transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
